import { apiRequest } from './client';

/** Notification centre (SRS #99/#100/#101/#138). */

const BASE = '/api/v1/notifications';
const TOKEN_KEY = 'policyora_token';

function opts(extra: RequestInit = {}): RequestInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return { ...extra, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra.headers || {}) } };
}

export type Severity = 'info' | 'warning' | 'critical';
export type Group = 'billing' | 'upgrades' | 'support' | 'team' | 'alerts' | 'security' | 'announcements';

export interface Notification {
  id: number;
  kind: string;
  group: Group;
  severity: Severity;
  title: string;
  body: string | null;
  link: string | null;
  meta: Record<string, unknown> | null;
  email_sent: boolean;
  read: boolean;
  read_at: string | null;
  dismissed: boolean;
  created_at: string | null;
}

export interface Summary { unread: number; banner: Notification | null }
export interface ListResult { notifications: Notification[]; unread: number; groups: Record<Group, string> }
export interface Preferences { email_enabled: boolean; muted_groups: Group[]; groups: Record<Group, string>; always_email: Group[] }

export const getSummary = () => apiRequest<Summary>(`${BASE}/summary`, opts());
export const listNotifications = (p: { unread?: boolean; group?: Group | ''; limit?: number; offset?: number } = {}) => {
  const q = new URLSearchParams();
  if (p.unread) q.set('unread', 'true');
  if (p.group) q.set('group', p.group);
  if (p.limit) q.set('limit', String(p.limit));
  if (p.offset) q.set('offset', String(p.offset));
  const qs = q.toString();
  return apiRequest<ListResult>(`${BASE}${qs ? `?${qs}` : ''}`, opts());
};
export const markRead = (ids: number[]) => apiRequest<{ updated: number }>(`${BASE}/read`, opts({ method: 'POST', body: JSON.stringify({ ids }) }));
export const markAllRead = () => apiRequest<{ updated: number }>(`${BASE}/read-all`, opts({ method: 'POST' }));
export const dismiss = (id: number) => apiRequest<{ ok: boolean }>(`${BASE}/${id}/dismiss`, opts({ method: 'POST' }));
export const getPreferences = () => apiRequest<Preferences>(`${BASE}/preferences`, opts());
export const setPreferences = (p: { email_enabled?: boolean; muted_groups?: Group[] }) =>
  apiRequest<Preferences>(`${BASE}/preferences`, opts({ method: 'PUT', body: JSON.stringify(p) }));

export interface BroadcastRequest { title: string; body?: string; link?: string; severity?: Severity; plan_tiers?: string[]; email?: boolean }
export const adminBroadcast = (p: BroadcastRequest) =>
  apiRequest<{ recipients: number }>('/api/v1/admin/notifications/broadcast', opts({ method: 'POST', body: JSON.stringify(p) }));

// Design-system tokens so the banner follows the theme (SRS #8): the literal creams rendered as a
// bright block on every dark screen.
export const SEVERITY_COLOR: Record<Severity, { bg: string; fg: string; border: string }> = {
  info: { bg: 'var(--ds-info-bg)', fg: 'var(--ds-info)', border: 'var(--ds-info)' },
  warning: { bg: 'var(--ds-warning-bg)', fg: 'var(--ds-warning)', border: 'var(--ds-warning)' },
  critical: { bg: 'var(--ds-danger-bg)', fg: 'var(--ds-danger)', border: 'var(--ds-danger-border)' },
};

export function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
